<template>
  <div id="OperationsList" v-if="$route.name === 'Operations'">
    <actions
      :operationQuery="true"
      :queryConfig="operationsQueryConfig"
      :selectedTab="selectedTab"
      @filterTab="toggleFilter"
      @download="download($event)"
      @onOperationsFilter="onOperationsFilter($event)"
      @reloadData="onOperationsFilter($event)"
      @search="operationSearch"
    />

    <page-loader v-if="loading" :hasActions="true"></page-loader>

    <empty-state
      v-if="!getOperations.content || !getOperations.content.length"
      module="operation"
      title="Commencer à créer votre opération."
      description="Vous n'avez aucune opération."
      :addSimulation="true"
    />

    <div
      v-if="getOperations.content && getOperations.content.length && !loading"
    >
      <table class="giga-table" :aria-describedby="$t('all_operations')">
        <thead>
          <tr class="cursor-pointer">
            <th
              @click="sortTable('name')"
              class="sort"
              :class="sortBy === 'name' ? (sortDir ? 'desc' : 'asc') : ''"
            >
              {{ $t("name_operation") }}
            </th>
            <th>{{ $t("name_work") }}</th>
            <th>{{ $t("commitment_date") }}</th>
            <th>{{ $t("completion_date") }}</th>
            <th>Vol. Précaire</th>
            <th>Vol. Classique</th>
            <th>{{ $t("plug_cee") }}</th>
            <th class="giga-status_col">{{ $t("status") }}</th>
            <th>{{ $t("actions") }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="operation in getOperations.content">
            <tr class="cursor-pointer" :key="operation.id">
              <td>
                <p>{{ operation.siteName }}</p>
              </td>
              <td>
                <p>
                  {{ operation.nameReferenceClient }}
                </p>
              </td>
              <td>
                <p>
                  {{ operation.beginDate }}
                </p>
              </td>
              <td>
                <p>
                  {{ operation.endDate }}
                </p>
              </td>
              <td>
                <p>
                  {{ operation.precarityVolume | formatNumber
                  }}<span> kWh cumac</span>
                </p>
              </td>
              <td>
                <p>
                  {{ operation.classicVolume | formatNumber }}
                  <span> kWh cumac</span>
                </p>
              </td>
              <td>
                <p>
                  {{ operation.ficheNumber }}
                </p>
              </td>
              <td>
                <b-badge class="giga-badge" :class="getStatusClass(operation)">
                  {{
                    operation.isDraft
                      ? "Brouillon"
                      : $t(
                          (operation.operationType === "SIMULATION"
                            ? "simulation_status."
                            : "operation_status.") + operation.status
                        )
                  }}
                </b-badge>
              </td>
              <td class="operation-actions">
                <p class="dots-wrapper">
                  <span
                    class="dots-menu"
                    @click.stop="toggleDropdown(operation.id)"
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </p>
                <div
                  v-if="dropdownVisible === operation.id"
                  class="dropdown-content"
                >
                  <ul>
                    <li @click="operationDetails(operation.id)">
                      Détail d’opérations
                    </li>
                    <li @click="operationDocuments(operation.id)">
                      Documents d’opérations
                    </li>
                    <li @click="operationHistorique(operation.id)">
                      Historique
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <pagination
        v-if="rows >= perPage"
        :length="length"
        :rows="rows"
        :perPage="perPage"
        :currentPage="currentPage"
        @changePerPage="changePerPage"
        @changeCurrentPage="changeCurrentPage"
      />
    </div>
    <OperationDocuments
      modal-id="operationDocuments"
      :operation="operationForDocs"
    />
    <OperationHistorique
      modal-id="operationHistorique"
      :operation="operationForHist"
    />
  </div>
  <router-view v-else />
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Actions from "@/components/static/Actions.vue";
import PageLoader from "@/components/static/pageLoader.vue";
import EmptyState from "@/components/static/emptyState.vue";
import Pagination from "@/components/static/Pagination.vue";
import { queryConstant } from "@/shared/queryConstant";
import { helpers } from "@/shared/helper";
import { constants } from "@/shared/constants";
import { cloneDeep } from "lodash";
import operationHistorique from "./OperationHistorique.vue";
import OperationHistorique from "./OperationHistorique.vue";
import OperationDocuments from "./OperationDocuments.vue";
import { operationService } from "./services/operation.service";

export default {
  name: "Operations",
  components: {
    Actions,
    PageLoader,
    EmptyState,
    Pagination,
    OperationDocuments,
    OperationHistorique
  },
  data() {
    const _pageSize_10 = 10;
    const _pageSize_25 = 25;
    const _pageSize_50 = 50;
    return {
      scrollBarSettings: {
        scrollXMarginOffset: 50
      },
      operationsQueryConfig: queryConstant.OPERATIONS_CONFIG,
      perPage: _pageSize_10,
      currentPage: 1,
      length: [_pageSize_10, _pageSize_25, _pageSize_50],
      sortBy: "id",
      sortDir: true,
      search: "",
      selectedTab: constants.EntityType.ALL,
      customFilter: queryConstant.setDefaultCustomFilter(),
      dropdownVisible: null,
      USER_TYPE: constants.USER_TYPE,
      ROLE_TYPE: constants.ROLE_TYPE,
      showDocumentsModal: false,
      operationForDocs: {},
      operationForHist: {}
    };
  },
  computed: {
    ...mapGetters("account", { currentUser: "getCurrentUser" }),
    ...mapGetters("operation", ["getOperations"]),
    ...mapGetters("loader", { loading: "getLoading" }),
    rows() {
      return this.getOperations.totalElements || 0;
    },
    isAnyOfUserTypes() {
      return helpers.isAnyOfUserTypes();
    },
    isNotB2B2B() {
      return helpers.isNotB2B2B();
    }
  },
  methods: {
    ...mapActions("operation", [
      "fetchOperationsAction",
      "fetchOperationsAgenceAction",
      "getOperationsAdminAgence",
      "getOpChargeDeProjet",
      "getWorksChargeDeProjetById"
    ]),
    ...mapActions("notification", ["openModal", "closeModal"]),
    ...mapMutations({
      toggleSidebar: "leftsidebar/toggleSidebar",
      setSidebarShow: "leftsidebar/setSidebarShow"
    }),
    changePerPage(perPage) {
      this.currentPage = 1;
      this.perPage = perPage;
      this.fetchData();
    },
    changeCurrentPage(page) {
      this.currentPage = page;
      this.fetchData();
    },
    sortTable(column) {
      if (this.sortBy === column) {
        this.sortDir = !this.sortDir;
      } else {
        this.sortDir = false;
      }
      this.sortBy = column;
      this.fetchData();
    },
    toggleFilter(type) {
      this.selectedTab =
        this.selectedTab === type ? constants.EntityType.ALL : type;
      this.fetchData();
    },
    download() {
      const request = {
        currentPage: this.currentPage,
        perPage: this.perPage,
        sortBy: this.sortBy,
        sortDir: this.sortDir,
        customFilter: this.customFilter,
        search: this.search,
        tab: this.selectedTab
      };

      this.$store.commit("loader/showDownloadWorksOrSimulations");
      this.$store.commit("loader/setDownloadWorksOrSimulations", true);

      this.$nextTick(() => {
        import("@/views/Operations/services/operation.service").then(
          ({ operationService }) => {
            operationService
              .downloadOperations(
                request.currentPage,
                request.perPage,
                request.sortBy,
                request.sortDir,
                request.customFilter,
                request.search,
                request.tab
              )
              .finally(() => {
                this.$store.commit("loader/hideDownloadWorksOrSimulations");
                this.$store.commit(
                  "loader/setDownloadWorksOrSimulations",
                  false
                );
              });
          }
        );
      });
    },
    fetchData() {
      if (
        this.currentUser.clientType === this.USER_TYPE.B2B2B &&
        this.customFilter.rules.some(rule => rule.field === "agence") &&
        (this.currentUser.admin ||
          this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
      ) {
        const agence = this.customFilter.rules.find(
          rule => rule.field === "agence"
        );
        let customFilter = cloneDeep(this.customFilter);
        customFilter.rules = this.customFilter.rules.filter(
          rule => rule.field !== "agence"
        );
        const request = {
          agenceId: agence?.rules[0]?.value?.id,
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          search: this.search,
          customFilter: customFilter,
          tab: this.selectedTab
        };
        this.fetchOperationsAgenceAction(request);
      } else {
        const request = {
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          search: this.search,
          customFilter: this.customFilter,
          tab: this.selectedTab
        };
        this.fetchOperationsAction(request);
      }
    },
    getStatusClass(operation) {
      if (operation.operationType === "SIMULATION") {
        if (operation.isDraft) return "draft";
        if (operation.status === "NEW") return "inProgress";
        if (operation.status === "CARRIED_OUT") return "delivered";
        if (operation.status === "CANCEL") return "perdu";
      } else {
        if (operation.status === "VERIFICATION_IN_PROGRESS")
          return "inProgress";
        if (operation.status === "OPERATION_DELIVERED") return "delivered";
        if (operation.status === "OPERATION_FILED_TO_PNCEE") return "deposited";
        if (operation.status === "OPERATION_ABONDONED") return "abondoned";
        if (operation.status === "OPERATION_DELETED") return "deleted";
      }
      return "";
    },
    onOperationsFilter(event) {
      this.customFilter = event.filter.custom;
      this.fetchData();
    },
    operationSearch(search) {
      this.search = search;
      this.fetchData();
    },
    toggleDropdown(id) {
      this.dropdownVisible = this.dropdownVisible === id ? null : id;
    },
    closeDropdownOnClickOutside(event) {
      const operationActions = this.$el.querySelectorAll(".operation-actions");
      let isClickInside = false;
      operationActions.forEach(el => {
        if (el.contains(event.target)) {
          isClickInside = true;
        }
      });
      if (!isClickInside) {
        this.dropdownVisible = null;
      }
    },
    beforeDestroy() {
      document.removeEventListener("click", this.closeDropdownOnClickOutside);
    },
    operationDetails(idOperation) {
      this.$router.push({
        name: "Operation",
        params: { idOperation }
      });
    },
    operationDocuments(id) {
      this.operationForDocs = this.getOperations.content.find(o => o.id === id);
      this.dropdownVisible = null;
      this.openModal({ instance: this.$root, id: "operationDocuments" });
    },
    async operationHistorique(id) {
      const operation = this.getOperations.content.find(o => o.id === id);
      const history = await operationService.getOperationHistoryById(id);
      this.operationForHist = { ...operation, history };
      this.dropdownVisible = null;
      this.openModal({ instance: this.$root, id: "operationHistorique" });
    }
  },
  mounted() {
    const otherQueryConfig = cloneDeep(queryConstant.OPERATIONS_CONFIG);
    if (
      this.currentUser.clientType === this.USER_TYPE.B2B2B &&
      (this.currentUser.admin ||
        this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
    ) {
      otherQueryConfig.fields.push(queryConstant.AGENCY_FILTER_OPTION);
    }
    this.operationsQueryConfig = otherQueryConfig;
    this.fetchData();
    document.addEventListener("click", this.closeDropdownOnClickOutside);
  }
};
</script>
<style scoped>
.operation-actions {
  position: relative;
  width: 20%;
  min-width: 40px;
  text-align: left;

  .dots-menu {
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;
    width: 16px;
    margin: auto;

    span {
      width: 4px;
      height: 4px;
      background-color: #132d48;
      border-radius: 50%;
    }
  }

  .dropdown-content {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 5px;
    background-color: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    z-index: 1000;
    min-width: 180px;
    overflow: visible;

    ul {
      list-style: none;
      padding: 10px 0;
      margin: 0;

      li {
        padding: 10px 15px;
        font-size: 14px;
        color: #132d48;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
          background-color: #8c9dab;
        }
      }
    }
  }
}

tr {
  position: static;
}
</style>
